import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { useTodaysDate } from "@whoosh/utilities/src/dates";

import {
  timeOfDayFilterAtom,
  startDateFilterAtom,
  lessonTypeFilterAtom,
} from "../atoms";
import { LessonsFilters } from "./LessonsFilters";
import { LessonsSidebar_facility$key } from "./__generated__/LessonsSidebar_facility.graphql";
import { LessonsSidebar_session$key } from "./__generated__/LessonsSidebar_session.graphql";

type Props = {
  facilityRef: LessonsSidebar_facility$key | null;
  sessionRef: LessonsSidebar_session$key | null;
};

export const LessonsSidebar = ({ facilityRef, sessionRef }: Props) => {
  const facility = useFragment(LessonsSidebarFragmentString, facilityRef);
  const session = useFragment(LessonsSidebarSessionFragmentString, sessionRef);

  const [timeOfDayFilter, setTimeOfDayFilter] = useAtom(timeOfDayFilterAtom);
  const [startDateFilter, setStartDateFilter] = useAtom(startDateFilterAtom);
  const [lessonTypeIdFilter, setLessonTypeIdFilter] =
    useAtom(lessonTypeFilterAtom);

  const today = useTodaysDate();

  const onLessonTypeFilterChange = useCallback(
    (lessonTypeId: string, defaultDurationMins: number) => {
      setLessonTypeIdFilter({
        id: lessonTypeId,
        defaultDuration: defaultDurationMins,
      });
    },
    [setLessonTypeIdFilter]
  );

  const onTimeOfDayFilterChange = useCallback(
    ({ timeGTE, timeLTE }: { timeGTE?: string; timeLTE?: string }) => {
      if (timeGTE === undefined && timeLTE === undefined) {
        setTimeOfDayFilter(undefined);
      } else {
        setTimeOfDayFilter({ timeGTE, timeLTE });
      }
    },
    [setTimeOfDayFilter]
  );

  const onStartDateFilterChange = useCallback(
    (date: string) => {
      setStartDateFilter(date);
    },
    [setStartDateFilter]
  );

  return (
    <LessonsFilters
      sessionRef={session ?? null}
      facilityRef={facility ?? null}
      selectedLessonTypeId={lessonTypeIdFilter?.id}
      selectedDate={startDateFilter || today}
      selectedTimeOfDay={timeOfDayFilter}
      onDateFilterChange={onStartDateFilterChange}
      onLessonTypeFilterChange={onLessonTypeFilterChange}
      onTimeOfDayFilterChange={onTimeOfDayFilterChange}
    />
  );
};

const LessonsSidebarFragmentString = graphql`
  fragment LessonsSidebar_facility on Facility {
    ...LessonsFiltersFragment_facility
  }
`;

const LessonsSidebarSessionFragmentString = graphql`
  fragment LessonsSidebar_session on Session {
    ...LessonsFiltersFragment_session
  }
`;
